import type { AssignmentSuggestion, Page } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../../shared/shared.api';
import { toListParams } from '../../../shared/policies/list-params';
import { useListQuery } from '../../../shared/hooks/use-list-query';
import { listAssignmentSuggestions } from '../dispatch.api';
import { suggestionPickerQuerySchema, type SuggestionPickerQuery } from '../policies/suggestion-picker-query';
import type { SuggestionWindow } from '../policies/suggestion-window.policy';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type SuggestionsState =
  | { status: 'loading' }
  | { status: 'loaded'; page: Page<AssignmentSuggestion>; queryKey: string }
  | { status: 'forbidden' }
  | { status: 'not-available'; message: string }
  | { status: 'error' };

const keyOf = (query: SuggestionPickerQuery) => toListParams(query).toString();

function toErrorState(err: unknown): SuggestionsState {
  if (!(err instanceof ApiError)) return { status: 'error' };
  if (err.status === 403) return { status: 'forbidden' };
  if (err.status === 404) return { status: 'not-available', message: 'This trip does not exist.' };
  if (err.status === 409) return { status: 'not-available', message: conflictMessage(err.body) };
  return { status: 'error' };
}

/** The server says why a trip cannot be offered crews (already assigned, no route); show its words. */
function conflictMessage(body: unknown): string {
  const message = typeof body === 'object' && body !== null && 'message' in body ? (body as { message: unknown }).message : null;
  return typeof message === 'string' ? message : 'This trip is not waiting for a crew.';
}

/** The page of suggested crews the URL's query asks for, re-fetched whenever that query changes or `reload` is called. */
export function useAssignmentSuggestions(tripId: string, window: SuggestionWindow) {
  const list = useListQuery(suggestionPickerQuerySchema);
  const { query } = list;
  const [state, setState] = useState<SuggestionsState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    listAssignmentSuggestions(tripId, query, window)
      .then((page) => {
        if (!cancelled) setState({ status: 'loaded', page, queryKey: keyOf(query) });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, [tripId, query, window]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf(query);
  return { state, list, isRefreshing };
}
